import { createAsyncThunk, createSlice, createSelector } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { getData } from 'features/dinamic/dinamicApi';
import { setLoading } from 'features/loader';
import { setDataToast, setVisibleToast } from 'features/toast';
import AlertInterface from 'features/toast/toastInterface';

interface EvaluacionListItem {
  idEvaluacion: number;
  idModulo: number | null;
  nombreModulo: string | null;
  idCiclo: number;
  nombreCiclo: string | null;
  nombre: string;
  contextoTipo: 'ciclo' | 'modulo' | 'sesion' | 'general';
  asignacionTipo: 'ciclo' | 'modulo_direct' | 'modulo_rel' | 'sesion' | 'general';
  contextoDescripcion: string;
  modulosAsignados: Array<{ idModulo: number; nombre: string }>;
  sesionNombre?: string | null;
  sesionFecha?: string | null;
  fechaInicioEval: string;
  fechaVencimientoEval: string;
  creditos: number | null;
  mensajeInicial: string;
  mensajeFinal: string;
  esEncuesta: boolean;
  idEvaluacionIntento: number;
  estado: string;
  fechaInicioIntento: string;
  fechaFinIntento: string;
  puntajeObtenido: number | null;
  porcentaje: number | null;
  diasRestantes: number;
}

interface EvaluacionDetalle {
  idIntento: number;
  estado: number;
  fechaInicio: string;
  fechaFin: string;
  puntajeTotal: number | null;
  puntajeObtenido: number | null;
  porcentaje: number | null;
  ultimaPregunta: number;
  idEvaluacion: number;
  nombre: string;
  mensajeInicial: string;
  mensajeFinal: string;
  fechaInicioEval: string;
  fechaVencimientoEval: string;
  preguntas: PreguntaDetalle[];
}

interface PreguntaDetalle {
  idPregunta: number;
  orden: number;
  titulo: string;
  descripcion: string;
  punteo: number | null;
  idTipoPregunta: number;
  idEvaluacionRespuesta: number;
  idRespuestaSeleccionada: number;
  respuestaAbierta: string;
  puntajeObtenido: number | null;
  esCorrecta: boolean | null;
  respondida: boolean;
  fechaRespuesta: string;
  urlReferencia?: string | null;
  opciones: Array<{
    idRespuesta: number;
    descripcion: string;
  }>;
}

interface EvaluacionResultado {
  idEvaluacionIntento: number;
  estado: number;
  fechaInicio: string;
  fechaFin: string;
  puntajeTotal: number | null;
  puntajeObtenido: number | null;
  porcentaje: number | null;
  nombre: string;
  mensajeInicial: string;
  mensajeFinal: string;
  preguntas: Array<{
    idPregunta: number;
    orden: number;
    titulo: string;
    descripcion: string;
    punteo: number | null;
    idTipoPregunta: number;
    idRespuestaSeleccionada: number | null;
    respuestaAbierta: string;
    puntajeObtenido: number | null;
    esCorrecta: boolean | null;
    retroalimentacion: string;
    respuestaCorrectaTexto: string | null;
    respuestaCorrectaId: number | null;
    respuestaSeleccionadaTexto: string | null;
    urlReferencia?: string | null;
    opciones: Array<{
      idRespuesta: number;
      descripcion: string;
    }>;
  }>;
}

interface AlumnoEvaluacionState {
  status: string;
  error: string;
  evaluacionList: EvaluacionListItem[];
  evaluacionDetalle: EvaluacionDetalle | null;
  evaluacionResultado: EvaluacionResultado | null;
  filtros: {
    idCiclo: number | null;
    idModulo: number | null;
    estado: string | null;
  };
  intentoActivo: {
    idEvaluacionIntento: number | null;
    preguntaActual: number;
    totalPreguntas: number;
  };
  lastQueryKey: string | null;
  currentQueryKey: string | null;
}

const initialState: AlumnoEvaluacionState = {
  status: 'idle',
  error: '',
  evaluacionList: [],
  evaluacionDetalle: null,
  evaluacionResultado: null,
  filtros: {
    idCiclo: null,
    idModulo: null,
    estado: null,
  },
  intentoActivo: {
    idEvaluacionIntento: null,
    preguntaActual: 0,
    totalPreguntas: 0,
  },
  lastQueryKey: null,
  currentQueryKey: null,
};

const normalizeEvaluaciones = (items: any[]): EvaluacionListItem[] =>
  items.map((item: any) => ({
    ...item,
    contextoTipo: item.contextoTipo ?? 'general',
    asignacionTipo: item.asignacionTipo ?? 'general',
    contextoDescripcion: item.contextoDescripcion ?? '',
    modulosAsignados: Array.isArray(item.modulosAsignados) ? item.modulosAsignados : [],
    sesionNombre: item.sesionNombre ?? null,
    sesionFecha: item.sesionFecha ?? null,
  }));

const parseOpciones = (value: any): Array<{ idRespuesta: number; descripcion: string }> => {
  if (Array.isArray(value)) {
    return value as Array<{ idRespuesta: number; descripcion: string }>;
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? (parsed as Array<{ idRespuesta: number; descripcion: string }>) : [];
    } catch (_) {
      return [] as Array<{ idRespuesta: number; descripcion: string }>;
    }
  }

  return [] as Array<{ idRespuesta: number; descripcion: string }>;
};

const normalizeEvaluacionResultadoData = (data: any): EvaluacionResultado | null => {
  if (!data) {
    return null;
  }

  const preguntasNormalizadas = Array.isArray(data.preguntas)
    ? data.preguntas.map((pregunta: any) => ({
        ...pregunta,
        respuestaSeleccionadaTexto: pregunta?.respuestaSeleccionadaTexto ?? null,
        opciones: parseOpciones(pregunta?.opciones),
      }))
    : [];

  return {
    ...data,
    preguntas: preguntasNormalizadas,
  } as EvaluacionResultado;
};

export const fetchEvaluacionList = createAsyncThunk(
  'alumnoEvaluacion/fetchList',
  async (params: any, { dispatch, getState }) => {
    dispatch(setLoading(true));
    const { result, message, data } = await getData(
      { idsp: 217, ...params },
      getState,
      dispatch
    );
    dispatch(setLoading(false));

    if (result) {
      if (data === null || data === undefined) {
        return [];
      }
      return Array.isArray(data) ? data : [data];
    } else {
      const alert: AlertInterface = {
        title: 'ERROR',
        message: message || 'Error al obtener evaluaciones',
        data: [],
        type: 'Crimson',
        time: Date.now()
      };
      dispatch(setDataToast(alert));
      dispatch(setVisibleToast(true));
      setTimeout(() => {
        dispatch(setVisibleToast(false));
      }, 4000);
      return [];
    }
  }
);

export const iniciarEvaluacion = createAsyncThunk(
  'alumnoEvaluacion/iniciarEvaluacion',
  async (params: { idEvaluacion: number }, { dispatch, getState }) => {
    dispatch(setLoading(true));
    const { result, message, data } = await getData(
      { idsp: 218, ...params },
      getState,
      dispatch
    );
    dispatch(setLoading(false));

    if (result) {
      const alertSuccess: AlertInterface = {
        title: 'ÉXITO',
        message: message || 'Evaluación iniciada correctamente',
        data: [],
        type: 'LimeGreen',
        time: Date.now()
      };
      dispatch(setDataToast(alertSuccess));
      dispatch(setVisibleToast(true));
      setTimeout(() => {
        dispatch(setVisibleToast(false));
      }, 4000);
      return data;
    } else {
      const alertError: AlertInterface = {
        title: 'ERROR',
        message: message || 'Error al iniciar evaluación',
        data: [],
        type: 'Crimson',
        time: Date.now()
      };
      dispatch(setDataToast(alertError));
      dispatch(setVisibleToast(true));
      setTimeout(() => {
        dispatch(setVisibleToast(false));
      }, 4000);
      throw new Error(message);
    }
  }
);

export const fetchEvaluacionDetalle = createAsyncThunk(
  'alumnoEvaluacion/fetchDetalle',
  async (params: { idEvaluacionIntento: number }, { dispatch, getState }) => {
    dispatch(setLoading(true));
    const { result, message, data } = await getData(
      { idsp: 219, ...params },
      getState,
      dispatch
    );
    dispatch(setLoading(false));

    if (result) {
      return data;
    } else {
      const alert: AlertInterface = {
        title: 'ERROR',
        message: message || 'Error al obtener detalle de evaluación',
        data: [],
        type: 'Crimson',
        time: Date.now()
      };
      dispatch(setDataToast(alert));
      dispatch(setVisibleToast(true));
      setTimeout(() => {
        dispatch(setVisibleToast(false));
      }, 4000);
      throw new Error(message);
    }
  }
);

export const guardarRespuesta = createAsyncThunk(
  'alumnoEvaluacion/guardarRespuesta',
  async (params: {
    idEvaluacionIntento: number;
    idPregunta: number;
    idRespuesta?: number;
    respuestaAbierta?: string;
  }, { dispatch, getState }) => {
    const { result, message } = await getData(
      { idsp: 220, ...params },
      getState,
      dispatch
    );

    if (result) {
      return true;
    } else {
      const alert: AlertInterface = {
        title: 'ERROR',
        message: message || 'Error al guardar respuesta',
        data: [],
        type: 'Crimson',
        time: Date.now()
      };
      dispatch(setDataToast(alert));
      dispatch(setVisibleToast(true));
      setTimeout(() => {
        dispatch(setVisibleToast(false));
      }, 4000);
      throw new Error(message);
    }
  }
);

export const finalizarEvaluacion = createAsyncThunk(
  'alumnoEvaluacion/finalizarEvaluacion',
  async (params: { idEvaluacionIntento: number }, { dispatch, getState }) => {
    dispatch(setLoading(true));
    const { result, message, data } = await getData(
      { idsp: 221, ...params },
      getState,
      dispatch
    );
    dispatch(setLoading(false));

    if (result) {
      const alertSuccess: AlertInterface = {
        title: 'ÉXITO',
        message: message || 'Evaluación finalizada correctamente',
        data: [],
        type: 'LimeGreen',
        time: Date.now()
      };
      dispatch(setDataToast(alertSuccess));
      dispatch(setVisibleToast(true));
      setTimeout(() => {
        dispatch(setVisibleToast(false));
      }, 4000);
      return data;
    } else {
      const alertError: AlertInterface = {
        title: 'ERROR',
        message: message || 'Error al finalizar evaluación',
        data: [],
        type: 'Crimson',
        time: Date.now()
      };
      dispatch(setDataToast(alertError));
      dispatch(setVisibleToast(true));
      setTimeout(() => {
        dispatch(setVisibleToast(false));
      }, 4000);
      throw new Error(message);
    }
  }
);

export const fetchEvaluacionResultado = createAsyncThunk(
  'alumnoEvaluacion/fetchResultado',
  async (params: { idEvaluacionIntento: number }, { dispatch, getState }) => {
    dispatch(setLoading(true));
    const { result, message, data } = await getData(
      { idsp: 222, ...params },
      getState,
      dispatch
    );
    dispatch(setLoading(false));

    if (result) {
      return data;
    } else {
      const alert: AlertInterface = {
        title: 'ERROR',
        message: message || 'Error al obtener resultado de evaluación',
        data: [],
        type: 'Crimson',
        time: Date.now()
      };
      dispatch(setDataToast(alert));
      dispatch(setVisibleToast(true));
      setTimeout(() => {
        dispatch(setVisibleToast(false));
      }, 4000);
      throw new Error(message);
    }
  }
);

export const alumnoEvaluacionSlice = createSlice({
  name: 'alumnoEvaluacion',
  initialState,
  reducers: {
    setFiltros: (state, action) => {
      state.filtros = { ...state.filtros, ...action.payload };
    },
    clearFiltros: (state) => {
      state.filtros = {
        idCiclo: null,
        idModulo: null,
        estado: null,
      };
    },
    setPreguntaActual: (state, action) => {
      state.intentoActivo.preguntaActual = action.payload;
    },
    setIntentoActivo: (state, action) => {
      state.intentoActivo.idEvaluacionIntento = action.payload;
    },
    clearEvaluacionDetalle: (state) => {
      state.evaluacionDetalle = null;
      state.intentoActivo = {
        idEvaluacionIntento: null,
        preguntaActual: 0,
        totalPreguntas: 0,
      };
    },
    clearEvaluacionResultado: (state) => {
      state.evaluacionResultado = null;
    },
    updateRespuestaLocal: (state, action) => {
      if (state.evaluacionDetalle) {
        const { idPregunta, idRespuesta, respuestaAbierta } = action.payload;
        const pregunta = state.evaluacionDetalle.preguntas.find(p => p.idPregunta === idPregunta);
        if (pregunta) {
          pregunta.idRespuestaSeleccionada = idRespuesta;
          pregunta.respuestaAbierta = respuestaAbierta;
          pregunta.respondida = true;
        }
      }
    },
    invalidateEvaluacionCache: (state) => {
      state.status = 'idle';
      state.lastQueryKey = null;
      state.currentQueryKey = null;
    },
    clearEvaluacionList: (state) => {
      state.evaluacionList = [];
      state.status = 'idle';
      state.lastQueryKey = null;
      state.currentQueryKey = null;
    },
    clearAlumnoEvaluacion: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEvaluacionList.pending, (state, action) => {
        state.status = 'loading';
        state.currentQueryKey = JSON.stringify(action.meta.arg ?? {}) ?? '{}';
      })
      .addCase(fetchEvaluacionList.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.evaluacionList = normalizeEvaluaciones(action.payload ?? []);
        state.lastQueryKey = state.currentQueryKey ?? state.lastQueryKey;
        state.currentQueryKey = null;
      })
      .addCase(fetchEvaluacionList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Error al cargar evaluaciones';
        state.lastQueryKey = state.currentQueryKey ?? state.lastQueryKey;
        state.currentQueryKey = null;
      })
      .addCase(iniciarEvaluacion.fulfilled, (state, action) => {
        state.intentoActivo.idEvaluacionIntento = action.payload.idEvaluacionIntento;
      })
      .addCase(fetchEvaluacionDetalle.fulfilled, (state, action) => {
        state.evaluacionDetalle = action.payload;
        if (action.payload.preguntas) {
          state.intentoActivo.totalPreguntas = action.payload.preguntas.length;
          state.intentoActivo.preguntaActual = 0;
        }
      })
      .addCase(finalizarEvaluacion.fulfilled, (state) => {
        state.intentoActivo = {
          idEvaluacionIntento: null,
          preguntaActual: 0,
          totalPreguntas: 0,
        };
      })
      .addCase(fetchEvaluacionResultado.fulfilled, (state, action) => {
        state.evaluacionResultado = normalizeEvaluacionResultadoData(action.payload);
      });
  },
});

export const {
  setFiltros,
  clearFiltros,
  setPreguntaActual,
  setIntentoActivo,
  clearEvaluacionDetalle,
  clearEvaluacionResultado,
  updateRespuestaLocal,
  invalidateEvaluacionCache,
  clearEvaluacionList,
  clearAlumnoEvaluacion,
} = alumnoEvaluacionSlice.actions;

export const selectEvaluacionList = (state: RootState) => state.alumnoEvaluacion.evaluacionList;
export const selectEncuestaList = createSelector([
  selectEvaluacionList,
], (evaluaciones) => evaluaciones.filter((item) => item.esEncuesta));
export const selectEvaluacionesAcademicas = createSelector([
  selectEvaluacionList,
], (evaluaciones) => evaluaciones.filter((item) => !item.esEncuesta));
export const selectEvaluacionDetalle = (state: RootState) => state.alumnoEvaluacion.evaluacionDetalle;
export const selectEvaluacionResultado = (state: RootState) => state.alumnoEvaluacion.evaluacionResultado;
export const selectFiltros = (state: RootState) => state.alumnoEvaluacion.filtros;
export const selectIntentoActivo = (state: RootState) => state.alumnoEvaluacion.intentoActivo;
export const selectStatus = (state: RootState) => state.alumnoEvaluacion.status;
export const selectLastQueryKey = (state: RootState) => state.alumnoEvaluacion.lastQueryKey;
export const selectCurrentQueryKey = (state: RootState) => state.alumnoEvaluacion.currentQueryKey;

export default alumnoEvaluacionSlice.reducer;
